/* eslint-disable @typescript-eslint/no-unused-vars */

import {
    SliceFactory,
    FetchCollectionData,
    FetchItemDataType,
    ApiCollectionResponse,
    filtersToElasticQuery,
    FetchCollectionInputType,
} from '@europrocurement/l2d-redux-utils';
import {
    ConfigurationParameters as AchatsConfigurationParameters,
    Configuration,
    FactureAchatApi,
    FactureAchatApiGetFacturesAchatsFactureAchatCollectionRequest,
    FactureAchatFactureAchatReadFactureAchatReadCalculatedItemsTimestampableBlameambleGroupAttachmentGroup,
    FactureAchatNoteJsonldFactureAchatReadFactureAchatReadItemFactureAchatLinkGieTimestampableBlameambleGroupAttachmentGroup,
} from '../../../openApi/ApiAchats';
import { ACHATS_REDUCER_NAME } from '../constants';
import { Order } from '../../../types/apiGenericTypes';

export type FactureAchatApiObject =
    FactureAchatFactureAchatReadFactureAchatReadCalculatedItemsTimestampableBlameambleGroupAttachmentGroup &
        Record<string, unknown>;

export type FactureAchat = FactureAchatApiObject;
export type FactureAchatNote =
    FactureAchatNoteJsonldFactureAchatReadFactureAchatReadItemFactureAchatLinkGieTimestampableBlameambleGroupAttachmentGroup;

export const FACTURES_ACHATS_SLICE_NAME = 'factureachat';

export const FACTURES_ACHATS_STATS_DATASOURCE_NAME = 'statsFacturesAchats';
export const FACTURES_ACHATS_PAUSE_DATASOURCE_NAME = 'pauseFactureAchats';
export const FACTURES_ACHATS_FROM_STATEMENT_DATASOURCE_NAME = 'statementFactureAchats';
export const FACTURES_ACHATS_FROM_PUBLISHER_REMINDER_DATASOURCE_NAME =
    'publisherReminderFactureAchats';

export const additionalFacturesAchatsDataSources: Array<string> = [
    FACTURES_ACHATS_STATS_DATASOURCE_NAME,
    FACTURES_ACHATS_PAUSE_DATASOURCE_NAME,
    FACTURES_ACHATS_FROM_STATEMENT_DATASOURCE_NAME,
    FACTURES_ACHATS_FROM_PUBLISHER_REMINDER_DATASOURCE_NAME,
];

const mapperFactureAchat = function (factureAchat: FactureAchatApiObject) {
    return {
        ...factureAchat,
    } as FactureAchat;
};

export function factureAchatsFiltersToElasticQuery({
    search,
    pagination,
    filters,
    orders,
    xIdSociete,
}: Omit<FetchCollectionInputType, 'rootstate' | 'options' | 'filters' | 'preFilters'> & {
    filters: {
        createdAt?: [Date, Date];
        updatedAt?: [Date, Date];
        dateFacture?: [Date, Date];
        codesRejets?: string[];
        categories?: string[];
        codesRejetsEtat?: string | string[];
        numeroFacture?: string;
        idEntiteFacturante?: number;
    } & Record<string, unknown>;
}) {
    const additionalMatch = [];

    if (filters.codesRejetsEtat) {
        let { codesRejetsEtat } = filters;
        if (typeof codesRejetsEtat === 'string') {
            codesRejetsEtat = [codesRejetsEtat];
        }
        additionalMatch.push(
            ...[
                ...codesRejetsEtat.map((item: string) => ({
                    nested: {
                        path: 'codesRejets',
                        query: {
                            bool: {
                                must: [
                                    {
                                        match: {
                                            'codesRejets.etat': item,
                                        },
                                    },
                                ],
                            },
                        },
                    },
                })),
            ],
        );
    }

    if (filters.codesRejets) {
        additionalMatch.push(
            ...[
                ...filters.codesRejets.map((item: string) => ({
                    nested: {
                        path: 'codesRejets',
                        query: {
                            bool: {
                                must: [
                                    {
                                        match: {
                                            'codesRejets.codeRejet.code': item,
                                        },
                                    },
                                    {
                                        match: {
                                            'codesRejets.etat': 'todo',
                                        },
                                    },
                                ],
                            },
                        },
                    },
                })),
            ],
        );
    }

    if (filters.categories) {
        additionalMatch.push(
            ...[
                ...filters.categories.map((item: string) => ({
                    nested: {
                        path: 'codesRejets',
                        query: {
                            bool: {
                                must: [
                                    {
                                        match: {
                                            'codesRejets.codeRejet.categorie.categorie': item,
                                        },
                                    },
                                    {
                                        match: {
                                            'codesRejets.etat': 'todo',
                                        },
                                    },
                                ],
                            },
                        },
                    },
                })),
            ],
        );
    }

    if (search && search !== '') {
        additionalMatch.push({
            query_string: {
                analyzer: 'pattern',
                query: `${search.replace(/\//g, '\\/')}+`,
                fields: [
                    'numeroFacture^3',
                    'lignes.numeroDossier',
                    'lignes.numeroPrestation',
                    'libelleFacturante',
                    'lignes.libelleAnnonceur',
                ],
            },
        });
        // eslint-disable-next-line no-param-reassign
        orders = [];
    }

    const {
        createdBy,
        updatedBy,
        createdAt,
        updatedAt,
        dateFacture,
        numeroFacture,
        linkedCodeRejets,
        idEntiteFacturante,
        ...innerFilters
    } = filters;

    if (numeroFacture) {
        additionalMatch.push({
            term: {
                'numeroFacture.keyword': numeroFacture,
            },
        });
    }

    if (idEntiteFacturante) {
        additionalMatch.push({
            term: {
                idEntiteFacturante,
            },
        });
    }

    if (createdBy && createdBy !== '') {
        additionalMatch.push({
            term: {
                'createdBy.keyword': createdBy,
            },
        });
    }

    if (updatedBy && updatedBy !== '') {
        additionalMatch.push({
            term: {
                'updatedBy.keyword': updatedBy,
            },
        });
    }

    if (createdAt && createdAt.length === 2) {
        additionalMatch.push({
            range: {
                createdAt: {
                    gt: createdAt[1],
                    lt: createdAt[0],
                },
            },
        });
    }

    if (updatedAt && updatedAt.length === 2) {
        additionalMatch.push({
            range: {
                updatedAt: {
                    gt: updatedAt[1],
                    lt: updatedAt[0],
                },
            },
        });
    }

    if (dateFacture && dateFacture.length === 2) {
        additionalMatch.push({
            range: {
                dateFacture: {
                    gt: dateFacture[1],
                    lt: dateFacture[0],
                },
            },
        });
    }

    if (linkedCodeRejets === false) {
        additionalMatch.push({
            term: {
                countCodesRejets: 0,
            },
        });
    } else if (linkedCodeRejets === true) {
        additionalMatch.push({
            range: {
                countCodesRejets: {
                    gt: 0,
                },
            },
        });
    }

    // exclude codesRejets and categories from innerFilters
    const { codesRejets, categories, ...cleanedInnerFilters } = innerFilters;

    const requestParameters: FactureAchatApiGetFacturesAchatsFactureAchatCollectionRequest = {
        xIdSociete,
        page: pagination.page + 1,
        itemsPerPage: pagination.itemsPerPage,
        search: filtersToElasticQuery(
            {
                idSociete: xIdSociete,
                ...cleanedInnerFilters,
            },
            additionalMatch,
            orders,
        ),
    };
    return requestParameters;
}

export function createFactureAchatSlice(configuration: AchatsConfigurationParameters) {
    const conf = new Configuration(configuration);
    const api = new FactureAchatApi(conf);

    const fetchFactureAchatsCollection: FetchCollectionData<FactureAchatApiObject> = ({
        search,
        pagination,
        filters,
        orders,
        xIdSociete,
        rootstate,
        preFilters,
    }) => {
        const parameters = factureAchatsFiltersToElasticQuery({
            search,
            pagination,
            filters,
            orders,
            xIdSociete,
            preFilters,
        });

        return api
            .getFacturesAchatsFactureAchatCollection(parameters)
            .then((res) => res.data) as Promise<ApiCollectionResponse<FactureAchatApiObject>>;
    };

    const fetchFactureAchatsItem: FetchItemDataType<FactureAchatApiObject> = ({
        idItem,
        search,
        pagination,
        filters,
        orders,
        xIdSociete,
        rootstate,
    }) =>
        api
            .getFactureAchatFactureAchatItem({
                id: String(idItem),
                xIdSociete,
            })
            .then((res) => res.data) as Promise<FactureAchatApiObject>;

    const { slice: factureAchatSlice, dataSourcesThunks: factureAchatDataSourcesThunks } =
        SliceFactory.createSlice<FactureAchatApiObject, FactureAchat>(
            FACTURES_ACHATS_SLICE_NAME,
            ACHATS_REDUCER_NAME,
            additionalFacturesAchatsDataSources,
            fetchFactureAchatsCollection,
            fetchFactureAchatsItem,
            mapperFactureAchat,
            {
                cloture: true,
                deleted: false,
            },
            undefined,
            [
                {
                    field: 'updatedAt',
                    value: 'desc',
                },
            ],
        );

    return { factureAchatSlice, factureAchatDataSourcesThunks };
}
